import React, { useState, useEffect, useCallback } from 'react';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import { UsageEventsApi } from '../../services/ApiService';
import { DateRangePicker, MetricCard, LoadingChart, EmptyState } from '../../components/analytics/SharedAnalyticsComponents';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import './FunnelBuilder.css';

const COMMON_EVENT_TYPES = [
  'PAGE_VIEW',
  'USER_LOGIN',
  'COURSE_SELECT',
  'ASSIGNMENT_VIEW',
  'ASSIGNMENT_COMPLETE',
  'DOCUMENT_VIEW',
  'DOCUMENT_DOWNLOAD',
  'NAV_CLICK',
  'THEME_CHANGE'
];

export default function FunnelBuilder() {
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionBased, setSessionBased] = useState(true);
  
  // Date range state
  const defaultTo = new Date();
  const defaultFrom = new Date(Date.now() - 30 * 86400000); // 30 days ago
  const [dateRange, setDateRange] = useState({
    from: defaultFrom.toISOString(),
    to: defaultTo.toISOString()
  });

  // Load available event types from backend
  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        const response = await UsageEventsApi.adminTopTypes({ 
          from: new Date(Date.now() - 90 * 86400000).toISOString(),
          to: new Date().toISOString(),
          limit: 50 
        });
        if (response.ok) {
          const types = await response.json();
          const uniqueTypes = [...new Set([...COMMON_EVENT_TYPES, ...types.map(t => t.eventType)])];
          setAvailableEvents(uniqueTypes.sort());
        } else {
          setAvailableEvents(COMMON_EVENT_TYPES);
        }
      } catch (err) {
        console.error('Failed to load event types:', err);
        setAvailableEvents(COMMON_EVENT_TYPES);
      }
    };
    loadEventTypes();
  }, []);

  // Add step to funnel
  const addStep = (eventType) => {
    if (!selectedSteps.includes(eventType)) {
      setSelectedSteps([...selectedSteps, eventType]);
    }
  };

  // Remove step from funnel
  const removeStep = (index) => {
    setSelectedSteps(selectedSteps.filter((_, i) => i !== index));
  };

  // Move step up
  const moveStepUp = (index) => {
    if (index > 0) {
      const newSteps = [...selectedSteps];
      [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
      setSelectedSteps(newSteps);
    }
  };

  // Move step down
  const moveStepDown = (index) => {
    if (index < selectedSteps.length - 1) {
      const newSteps = [...selectedSteps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      setSelectedSteps(newSteps);
    }
  };

  // Analyze funnel
  const analyzeFunnel = useCallback(async () => {
    if (selectedSteps.length < 2) {
      setError('Please select at least 2 steps to analyze a funnel');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await AnalyticsApi.analyzeFunnel({
        steps: selectedSteps,
        from: dateRange.from,
        to: dateRange.to,
        sessionBased: sessionBased
      });
      setFunnelData(result);
    } catch (err) {
      setError(err.message || 'Failed to analyze funnel');
      setFunnelData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSteps, dateRange, sessionBased]);

  // Auto-analyze when steps or settings change
  useEffect(() => {
    if (selectedSteps.length >= 2) {
      const timer = setTimeout(() => analyzeFunnel(), 500);
      return () => clearTimeout(timer);
    } else {
      setFunnelData(null);
    }
  }, [selectedSteps, dateRange, sessionBased, analyzeFunnel]);

  // Prepare chart data
  const chartData = funnelData?.metrics?.map((metric, index) => ({
    name: metric.stepName,
    stepIndex: index + 1,
    users: metric.usersCompleted,
    conversionRate: metric.conversionRate?.toFixed(1),
    dropOffRate: metric.dropOffRate?.toFixed(1),
    overallRate: metric.overallConversionRate?.toFixed(1)
  })) || [];

  // Color scale for funnel visualization (green -> yellow -> red as conversion drops)
  const getBarColor = (conversionRate) => {
    if (conversionRate >= 70) return '#28a745'; // Green
    if (conversionRate >= 40) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  return (
    <div className="funnel-builder">
      <div className="page-header">
        <h2>Funnel Analysis Builder</h2>
        <p className="subtitle">Create custom conversion funnels to analyze user journey drop-offs</p>
      </div>

      {/* Configuration Panel */}
      <div className="config-panel">
        <div className="config-section">
          <label className="config-label">
            <input 
              type="checkbox" 
              checked={sessionBased} 
              onChange={(e) => setSessionBased(e.target.checked)}
            />
            <span>Session-based funnel (all steps must occur in same session)</span>
          </label>
        </div>

        <div className="config-section">
          <DateRangePicker 
            from={new Date(dateRange.from)}
            to={new Date(dateRange.to)}
            onChange={({ from, to }) => setDateRange({ 
              from: from ? new Date(from).toISOString() : dateRange.from, 
              to: to ? new Date(to).toISOString() : dateRange.to 
            })}
          />
        </div>
      </div>

      <div className="builder-container">
        {/* Event Selection Panel */}
        <div className="event-selection-panel">
          <h3>Available Events</h3>
          <div className="event-list">
            {availableEvents.map(eventType => (
              <button
                key={eventType}
                className="event-button"
                onClick={() => addStep(eventType)}
                disabled={selectedSteps.includes(eventType)}
              >
                + {eventType}
              </button>
            ))}
          </div>
        </div>

        {/* Funnel Steps Panel */}
        <div className="funnel-steps-panel">
          <h3>Funnel Steps ({selectedSteps.length})</h3>
          {selectedSteps.length === 0 && (
            <div className="empty-funnel">
              <p>Click events on the left to build your funnel</p>
              <p className="hint">Add at least 2 steps to analyze</p>
            </div>
          )}
          
          <div className="steps-list">
            {selectedSteps.map((step, index) => (
              <div key={index} className="funnel-step-item">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">
                  <div className="step-name">{step}</div>
                  {funnelData?.metrics?.[index] && (
                    <div className="step-metrics">
                      <span className="metric">
                        👥 {funnelData.metrics[index].usersCompleted?.toLocaleString()} users
                      </span>
                      <span className="metric conversion-rate">
                        ✓ {funnelData.metrics[index].conversionRate?.toFixed(1)}%
                      </span>
                      {index > 0 && (
                        <span className="metric drop-off">
                          ↓ {funnelData.metrics[index].dropOffRate?.toFixed(1)}% drop-off
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="step-actions">
                  <button 
                    className="action-btn" 
                    onClick={() => moveStepUp(index)}
                    disabled={index === 0}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button 
                    className="action-btn" 
                    onClick={() => moveStepDown(index)}
                    disabled={index === selectedSteps.length - 1}
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button 
                    className="action-btn remove" 
                    onClick={() => removeStep(index)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Results Section */}
      {selectedSteps.length >= 2 && (
        <div className="results-section">
          <div className="results-header">
            <h3>Funnel Analysis Results</h3>
            {funnelData && (
              <div className="overall-metrics">
                <MetricCard 
                  label="Total Entered"
                  value={funnelData.totalEntered?.toLocaleString()}
                />
                <MetricCard 
                  label="Total Completed"
                  value={funnelData.totalCompleted?.toLocaleString()}
                />
                <MetricCard 
                  label="Overall Conversion"
                  value={`${funnelData.overallConversionRate?.toFixed(1)}%`}
                />
              </div>
            )}
          </div>

          {loading && <LoadingChart />}
          
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {!loading && !error && funnelData && (
            <>
              {/* Funnel Visualization */}
              <div className="chart-container">
                <h4>Conversion Funnel</h4>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 150, right: 50, top: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={140} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ 
                              background: 'var(--card-bg)', 
                              border: '1px solid var(--border-light)', 
                              borderRadius: '8px', 
                              padding: '0.75rem',
                              fontSize: '0.85rem'
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{data.name}</div>
                              <div>Users: {data.users?.toLocaleString()}</div>
                              <div>Conversion: {data.conversionRate}%</div>
                              <div>Overall: {data.overallRate}%</div>
                              {data.stepIndex > 1 && <div>Drop-off: {data.dropOffRate}%</div>}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="users" radius={[0, 8, 8, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(parseFloat(entry.conversionRate))} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Detailed Metrics Table */}
              <div className="metrics-table-container">
                <h4>Detailed Step Metrics</h4>
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Step</th>
                      <th>Event Type</th>
                      <th>Users Entered</th>
                      <th>Users Completed</th>
                      <th>Dropped Off</th>
                      <th>Step Conversion</th>
                      <th>Overall Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelData.metrics.map((metric, index) => (
                      <tr key={index}>
                        <td className="step-num">{index + 1}</td>
                        <td className="event-type">{metric.stepName}</td>
                        <td>{metric.usersEntered?.toLocaleString()}</td>
                        <td>{metric.usersCompleted?.toLocaleString()}</td>
                        <td>{metric.usersDroppedOff?.toLocaleString()}</td>
                        <td>
                          <span className={`rate-badge ${metric.conversionRate >= 70 ? 'good' : metric.conversionRate >= 40 ? 'medium' : 'poor'}`}>
                            {metric.conversionRate?.toFixed(1)}%
                          </span>
                        </td>
                        <td>{metric.overallConversionRate?.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loading && !error && selectedSteps.length >= 2 && !funnelData && (
            <EmptyState message="Click 'Analyze Funnel' to see results" />
          )}
        </div>
      )}
    </div>
  );
}
